import { useState, useEffect } from 'react'
import { useAngularServices } from '@/react/components'
import { useRouter } from '@/react/hooks'
import { CurrentUserType } from '@/react/types/documentation'
import {
  EmployeeOrGroup,
  Training,
  TrainingEmployeeGroup,
} from '@/react/types/trainings'

export const useTrainingEmployeeListState = () => {
  const { Api, CurrentUser } = useAngularServices()
  const [order, setOrder] = useState('name,first_name,last_name')
  const [training, setTraining] = useState<Training>({} as Training)
  const [employeeList, setEmployeeList] = useState<EmployeeOrGroup[]>([])
  const variableColor = (CurrentUser as CurrentUserType).getClientSettings()
    .web_primary_color
  const { stateService } = useRouter()
  const [page, setPage] = useState(1)
  const [isAllChecked, setAllChecked] = useState<boolean>(false)
  const [search, setSearch] = useState('')
  const [count, setCount] = useState(20)
  const [refresh, setRefresh] = useState(false)
  const [isPopUpVisible, setPopUpVisible] = useState(false)
  const [listToAssign, setListToAssign] = useState<number[]>([])
  const [listToUnassign, setListToUnassign] = useState<number[]>([])
  const [wholeIdsList, setWholeIdsList] = useState<number[]>([])
  const [alreadyAssigned, setAlreadyAssigned] = useState<number[]>([])

  const handleCheckedAll = () => {
    if (!isAllChecked) {
      setAllChecked(true)
      setListToUnassign([])
      setListToAssign(wholeIdsList)
    } else {
      setAllChecked(false)
      setListToAssign([])
      setListToUnassign(wholeIdsList)
    }
    setEmployeeList(
      employeeList.map((emp) => {
        emp.assigned = !isAllChecked
        return emp
      }),
    )
  }

  const handleTogglePopup = () => {
    setPopUpVisible((prev) => !prev)
  }

  const handleAssign = (employee: EmployeeOrGroup, isGroup: boolean) => {
    if (isGroup) {
      const groupUsers = (employee as TrainingEmployeeGroup).users
      const isThisGroupFullyAssigned = groupUsers.every((id) =>
        listToAssign.includes(id),
      )

      if (isThisGroupFullyAssigned) {
        setAllChecked(false)
        setListToAssign((prevList) =>
          prevList.filter((id) => !groupUsers.includes(id)),
        )
        setListToUnassign((prevList) => [...prevList, ...groupUsers])
      } else {
        setListToAssign((prevList) => {
          const newList = [...prevList, ...groupUsers]
          setAllChecked(newList.length === wholeIdsList.length)
          return newList
        })
        setListToUnassign((prevList) =>
          prevList.filter((id) => !groupUsers.includes(id)),
        )
      }
    } else {
      const condition = !listToAssign.includes(employee.id)

      if (condition) {
        setListToAssign((prevList) => {
          const newList = [...prevList, employee.id]
          setAllChecked(newList.length === wholeIdsList.length)
          return newList
        })
        setListToUnassign((prevList) =>
          prevList.filter((id) => id !== employee.id),
        )
      } else {
        setAllChecked(false)
        setListToAssign((prevList) =>
          prevList.filter((id) => id !== employee.id),
        )
        setListToUnassign((prevList) => [...prevList, employee.id])
      }
    }
  }

  const handleButtonClick = async () => {
    const usersToAssign = listToAssign.filter(
      (id) => !alreadyAssigned.includes(id),
    )

    const usersToUnassign = alreadyAssigned.filter(
      (id) => !listToAssign.includes(id),
    )

    if (usersToAssign.length > 0) {
      await Api.post('assign', {
        active: true,
        trainings: [training.id],
        users: usersToAssign,
      })
    }

    if (usersToUnassign.length > 0) {
      await Api.post('assign', {
        active: false,
        trainings: [training.id],
        users: usersToUnassign,
      })
    }

    stateService.go('trainings.assign.employees_list', {
      pageNumber: stateService.params.previousPageNumber,
      search: stateService.params.previousSearch,
    })
  }

  useEffect(() => {
    const getStartingData = async () => {
      const { data: trainingResponse } = (await Api.get(
        `trainings/${stateService.params.training}`,
        {},
      )) as any

      const { data: wholeListRes } = (await Api.get(
        'assign_training_set/user_ids',
        {
          is_active: true,
          training: stateService.params.training,
        },
      )) as any

      const { data: assignedListRes } = (await Api.get(
        'assign_training_set/user_ids',
        {
          training: stateService.params.training,
          is_assigned: true,
        },
      )) as any

      setTraining(trainingResponse)
      setWholeIdsList(wholeListRes)
      setListToAssign(assignedListRes)
      setAlreadyAssigned(assignedListRes)
    }
    void getStartingData()
  }, [])

  useEffect(() => {
    const getEmployeeData = async () => {
      const queryParams = {
        training: stateService.params.training,
        deleted: false,
        is_active: true,
        order,
        active: true,
        page,
        search,
      }

      const { data: employeeGroup } = await Api.get<{
        results: TrainingEmployeeGroup[]
        count: number
      }>('assign_training_set/user_set', queryParams)

      const filteredEmployeeGroups = employeeGroup.results.filter(
        (emp: TrainingEmployeeGroup) =>
          !('users' in emp) ||
          (Array.isArray(emp.users) && emp.users.length > 0),
      )

      setEmployeeList(filteredEmployeeGroups)
      setCount(employeeGroup.count)
      setRefresh(false)
    }
    void getEmployeeData()
  }, [search, page, order, refresh])

  return {
    order,
    training,
    employeeList,
    variableColor,
    page,
    isAllChecked,
    search,
    count,
    refresh,
    isPopUpVisible,
    listToAssign,
    listToUnassign,
    wholeIdsList,
    alreadyAssigned,
    setOrder,
    setPage,
    setSearch,
    setRefresh,
    setPopUpVisible,
    handleCheckedAll,
    handleTogglePopup,
    handleAssign,
    handleButtonClick,
  }
}
